/**
 * Panel displaying comments from a label version with note-adding capability
 * Supports threaded display of replies
 */
import React, { useState, useMemo, useEffect } from 'react';
import { authApi } from '../services/authApi';
import type { CommentResponse, CommentType } from '../types';

interface CommentsPanelProps {
  comments: CommentResponse[];
  onCommentUpdated?: (comment: CommentResponse) => void;
  onCommentClick?: (comment: CommentResponse) => void;
  selectedCommentId?: number | null;
  isLoading?: boolean;
  isSidebar?: boolean;
}

// Thread structure: a comment with its replies
interface CommentThread {
  comment: CommentResponse;
  replies: CommentResponse[];
}

/**
 * Organizes flat comment array into threaded structure.
 * Top-level comments (no parent) are grouped with their replies.
 */
function organizeIntoThreads(comments: CommentResponse[]): CommentThread[] {
  const threads: CommentThread[] = [];
  const commentMap = new Map<number, CommentResponse>();
  const replyMap = new Map<number, CommentResponse[]>();

  // First pass: build maps
  for (const comment of comments) {
    commentMap.set(comment.id, comment);
    if (comment.parent_comment_id) {
      const replies = replyMap.get(comment.parent_comment_id) || [];
      replies.push(comment);
      replyMap.set(comment.parent_comment_id, replies);
    }
  }

  // Second pass: build threads from top-level comments
  for (const comment of comments) {
    if (!comment.parent_comment_id) {
      // This is a top-level comment
      const replies = replyMap.get(comment.id) || [];
      // Sort replies by date (oldest first)
      replies.sort((a, b) => {
        const dateA = a.comment_date ? new Date(a.comment_date).getTime() : 0;
        const dateB = b.comment_date ? new Date(b.comment_date).getTime() : 0;
        return dateA - dateB;
      });
      threads.push({ comment, replies });
    }
  }

  return threads;
}

const commentTypeConfig: Record<CommentType, { label: string; color: string; icon: string }> = {
  review: { label: 'Review', color: 'bg-slate-100 text-slate-700', icon: '📝' },
  suggestion: { label: 'Suggestion', color: 'bg-purple-100 text-purple-700', icon: '💡' },
  question: { label: 'Question', color: 'bg-indigo-100 text-indigo-700', icon: '❓' },
  approval: { label: 'Approval', color: 'bg-green-100 text-green-700', icon: '✓' },
  change_request: { label: 'Change Request', color: 'bg-amber-100 text-amber-700', icon: '⚠' },
};

const CommentCard: React.FC<{
  comment: CommentResponse;
  onNoteAdded: (comment: CommentResponse) => void;
  onClick?: () => void;
  isSelected?: boolean;
  isCompact?: boolean;
  isReply?: boolean;
}> = ({ comment, onNoteAdded, onClick, isSelected, isCompact, isReply = false }) => {
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteText, setNoteText] = useState(comment.user_note || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const typeConfig = comment.comment_type
    ? commentTypeConfig[comment.comment_type]
    : { label: 'Comment', color: 'bg-slate-100 text-slate-700', icon: '💬' };

  const handleSaveNote = async () => {
    if (!noteText.trim()) return;

    setIsSaving(true);
    setError(null);

    try {
      const updated = await authApi.addCommentNote(comment.id, {
        note: noteText.trim(),
      });
      onNoteAdded(updated);
      setIsAddingNote(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save note');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelNote = () => {
    setNoteText(comment.user_note || '');
    setIsAddingNote(false);
    setError(null);
  };

  return (
    <div
      data-comment-card-id={comment.id}
      onClick={onClick}
      className={`
        bg-white border rounded-lg transition-all
        ${onClick ? 'cursor-pointer hover:shadow-md' : 'hover:shadow-sm'}
        ${isSelected ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-slate-200'}
        ${isCompact ? 'p-3' : 'p-4'}
        ${isReply ? 'ml-6 border-l-4 border-l-indigo-200' : ''}
      `}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${typeConfig.color}`}>
            {typeConfig.icon} {typeConfig.label}
          </span>
          {comment.author && (
            <span className="text-sm font-medium text-slate-700">
              {comment.author}
              {comment.author_initials && ` (${comment.author_initials})`}
            </span>
          )}
          {comment.comment_date && (
            <span className="text-xs text-slate-400">
              {new Date(comment.comment_date).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      {/* Comment text */}
      <p className="text-slate-800 text-sm leading-relaxed">{comment.comment_text}</p>

      {/* Context */}
      {comment.context_text && (
        <div className={`mt-3 p-2 rounded border-l-2 ${
          comment.context_is_deleted
            ? 'bg-red-50 border-red-300'
            : 'bg-slate-50 border-slate-300'
        }`}>
          <p className={`text-xs font-medium mb-1 ${
            comment.context_is_deleted ? 'text-red-600' : 'text-slate-500'
          }`}>
            {comment.context_is_deleted && '⚠️ '}
            Context{comment.context_is_deleted && ' (deleted text)'}
            {comment.context_section_type && ` (${comment.context_section_type})`}:
          </p>
          <p className={`text-xs italic line-clamp-3 ${
            comment.context_is_deleted ? 'text-red-600 line-through' : 'text-slate-600'
          }`}>{comment.context_text}</p>
        </div>
      )}

      {/* User note section */}
      <div className="mt-3 pt-3 border-t border-slate-100">
        {comment.user_note && !isAddingNote ? (
          <div className="bg-indigo-50 rounded p-2">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-indigo-700">Note</p>
              <button
                onClick={() => setIsAddingNote(true)}
                className="text-xs text-indigo-600 hover:text-indigo-800"
              >
                Edit
              </button>
            </div>
            <p className="text-sm text-indigo-900">{comment.user_note}</p>
            {comment.note_added_by && (
              <p className="text-xs text-indigo-500 mt-1">
                — {comment.note_added_by}
                {comment.note_added_at &&
                  `, ${new Date(comment.note_added_at).toLocaleDateString()}`}
              </p>
            )}
          </div>
        ) : isAddingNote ? (
          <div className="space-y-2">
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add your note about this comment..."
              rows={2}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            />
            {error && <p className="text-xs text-red-600">{error}</p>}
            <div className="flex justify-end gap-2">
              <button
                onClick={handleCancelNote}
                disabled={isSaving}
                className="px-3 py-1 text-xs text-slate-600 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNote}
                disabled={isSaving || !noteText.trim()}
                className="px-3 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save Note'}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setIsAddingNote(true)}
            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
          >
            + Add Note
          </button>
        )}
      </div>
    </div>
  );
};

const CommentsPanel: React.FC<CommentsPanelProps> = ({
  comments,
  onCommentUpdated,
  onCommentClick,
  selectedCommentId,
  isLoading = false,
  isSidebar = false,
}) => {
  const [filterType, setFilterType] = useState<CommentType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Auto-scroll to the selected comment card when it changes
  useEffect(() => {
    if (selectedCommentId) {
      const card = document.querySelector(`[data-comment-card-id="${selectedCommentId}"]`);
      card?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedCommentId]);

  // Filter comments first
  const filteredComments = comments.filter((comment) => {
    if (filterType !== 'all' && comment.comment_type !== filterType) {
      return false;
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        comment.comment_text.toLowerCase().includes(query) ||
        comment.author?.toLowerCase().includes(query) ||
        comment.context_text?.toLowerCase().includes(query) ||
        comment.user_note?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Organize filtered comments into threads
  const threads = useMemo(() => organizeIntoThreads(filteredComments), [filteredComments]);

  // Count total threads (top-level comments only) for display
  const topLevelCount = comments.filter(c => !c.parent_comment_id).length;
  const replyCount = comments.length - topLevelCount;

  const commentCounts = comments.reduce(
    (acc, comment) => {
      const type = comment.comment_type || 'review';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-slate-200 rounded w-1/4"></div>
          <div className="h-20 bg-slate-100 rounded"></div>
          <div className="h-20 bg-slate-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-800">
            Comments ({comments.length})
            {replyCount > 0 && (
              <span className="text-sm font-normal text-slate-500 ml-2">
                ({topLevelCount} threads, {replyCount} replies)
              </span>
            )}
          </h3>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex gap-1 flex-wrap">
            <button
              onClick={() => setFilterType('all')}
              className={`px-2 py-1 text-xs rounded-full transition-colors ${
                filterType === 'all'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              All
            </button>
            {Object.entries(commentTypeConfig).map(([type, config]) => {
              const count = commentCounts[type] || 0;
              if (count === 0) return null;
              return (
                <button
                  key={type}
                  onClick={() => setFilterType(type as CommentType)}
                  className={`px-2 py-1 text-xs rounded-full transition-colors ${
                    filterType === type
                      ? 'bg-indigo-600 text-white'
                      : `${config.color} hover:opacity-80`
                  }`}
                >
                  {config.label} ({count})
                </button>
              );
            })}
          </div>

          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search comments..."
            className="flex-1 min-w-[150px] px-3 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Comments list - threaded view */}
      <div className={`p-4 space-y-4 overflow-y-auto ${isSidebar ? 'max-h-[calc(100vh-300px)]' : 'max-h-[600px]'}`}>
        {threads.length > 0 ? (
          threads.map((thread) => (
            <div key={thread.comment.id} className="space-y-2">
              {/* Top-level comment */}
              <CommentCard
                comment={thread.comment}
                onNoteAdded={(updated) => onCommentUpdated?.(updated)}
                {...(onCommentClick && { onClick: () => onCommentClick(thread.comment) })}
                isSelected={selectedCommentId === thread.comment.id}
                isCompact={isSidebar}
              />
              {/* Replies */}
              {thread.replies.length > 0 && (
                <div className="space-y-2">
                  {thread.replies.map((reply) => (
                    <CommentCard
                      key={reply.id}
                      comment={reply}
                      onNoteAdded={(updated) => onCommentUpdated?.(updated)}
                      {...(onCommentClick && { onClick: () => onCommentClick(reply) })}
                      isSelected={selectedCommentId === reply.id}
                      isCompact={isSidebar}
                      isReply
                    />
                  ))}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-slate-500">
            {comments.length === 0 ? (
              <>
                <p className="font-medium">No comments yet</p>
                <p className="text-sm mt-1">
                  Upload a document with comments to see them here
                </p>
              </>
            ) : (
              <>
                <p className="font-medium">No matching comments</p>
                <p className="text-sm mt-1">Try adjusting your filters</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentsPanel;
